import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { api, ApiError, type Invitation, type Me, type SpaceSummary } from '../../lib/api'
import { CategoryMark } from './CategoryMark'
import { AccountSheet } from '../auth/AccountSheet'

type Sheet = 'aucune' | 'creer' | 'rejoindre' | 'compte'

/**
 * Planche 02 du cahier des charges — « Espaces ».
 *
 * Note de la planche : « L'espace actif est mémorisé et rouvert au lancement — cet écran n'est
 * pas un passage obligé. » Tant que la page d'espace n'existe pas (étape 5), cet écran reste le
 * point d'arrivée après connexion.
 */
export function SpacesScreen({ me }: { me: Me }) {
  const [sheet, setSheet] = useState<Sheet>('aucune')
  const [invitation, setInvitation] = useState<Invitation | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const refreshMe = () => queryClient.invalidateQueries({ queryKey: ['me'] })

  const activate = useMutation({
    mutationFn: api.setActiveSpace,
    onSuccess: async (_data, spaceId) => {
      await refreshMe()
      void navigate(`/espace/${spaceId}`)
    },
  })

  const invite = useMutation({
    mutationFn: api.inviteToSpace,
    onSuccess: setInvitation,
  })

  return (
    <main
      className="mx-auto min-h-dvh bg-surface pb-12"
      style={{ maxWidth: 'var(--layout-column)', paddingInline: 'var(--layout-gutter)' }}
    >
      <header className="flex items-center" style={{ height: 'var(--layout-header)' }}>
        <h1 className="font-serif text-screen">Espaces</h1>
      </header>

      {me.spaces.length === 0 ? (
        // « Aucun écran vide sans issue : tout état vide propose l'action suivante. »
        <p className="text-body text-ink-muted">
          Aucun espace pour l'instant. Créez-en un, ou rejoignez celui de quelqu'un avec son code.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {me.spaces.map((space) => (
            <SpaceRow
              key={space.id}
              space={space}
              onActivate={() =>
                space.isActive ? void navigate(`/espace/${space.id}`) : activate.mutate(space.id)
              }
              onInvite={() => invite.mutate(space.id)}
            />
          ))}
        </ul>
      )}

      {invitation ? (
        <InvitationPanel invitation={invitation} onClose={() => setInvitation(null)} />
      ) : null}

      <div className="mt-6 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => setSheet('creer')}
          className="flex w-full items-center gap-3 rounded-card border border-border bg-card px-4 text-card-title font-semibold"
          style={{ minHeight: 'var(--layout-touch-min)', paddingBlock: 16 }}
        >
          <span
            className="flex items-center justify-center rounded-field bg-accent-soft text-accent"
            style={{ width: 32, height: 32 }}
            aria-hidden="true"
          >
            +
          </span>
          Créer un espace
        </button>

        <button
          type="button"
          onClick={() => setSheet('rejoindre')}
          className="w-full rounded-card border border-border bg-card px-4 text-card-title font-semibold"
          style={{ minHeight: 'var(--layout-touch-min)', paddingBlock: 16 }}
        >
          Rejoindre avec un code
        </button>
      </div>

      <div className="mt-8 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => setSheet('compte')}
          className="text-left text-meta font-semibold text-accent"
        >
          {/* « Mon compte » et non « Changer mon mot de passe » : la feuille porte aussi le
              nom affiché et le code des fiches protégées, et son ancien libellé n'en annonçait
              qu'un tiers. */}
          Mon compte
        </button>

        {/* L'administration n'est pas une porte séparée : c'est une section de l'application,
            visible du seul compte qui en porte le rôle. */}
        {me.isAdmin ? (
          <Link to="/admin" className="text-meta font-semibold text-accent">
            Administration
          </Link>
        ) : null}
      </div>

      {sheet === 'compte' ? <AccountSheet me={me} onClose={() => setSheet('aucune')} /> : null}

      {sheet === 'creer' ? (
        <NameSheet
          title="Créer un espace"
          label="Nom de l'espace"
          placeholder="Nous deux"
          action="Créer"
          mutate={api.createSpace}
          onDone={() => {
            setSheet('aucune')
            void refreshMe()
          }}
          onCancel={() => setSheet('aucune')}
        />
      ) : null}

      {sheet === 'rejoindre' ? (
        <NameSheet
          title="Rejoindre un espace"
          label="Code d'invitation"
          placeholder="ABCD2345"
          action="Rejoindre"
          mutate={api.joinSpace}
          onDone={() => {
            setSheet('aucune')
            void refreshMe()
          }}
          onCancel={() => setSheet('aucune')}
        />
      ) : null}
    </main>
  )
}

function SpaceRow({
  space,
  onActivate,
  onInvite,
}: {
  space: SpaceSummary
  onActivate: () => void
  onInvite: () => void
}) {
  return (
    <li
      className="rounded-card bg-card"
      style={{
        // L'espace actif est cerné d'un trait d'accent de 2 px, comme sur la planche.
        border: space.isActive ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
      }}
    >
      {/* La brique entière ouvre l'espace. Elle ne portait qu'un petit lien « Ouvrir » de 13 px :
          une cible minuscule pour un geste courant, et la carte au-dessus ne réagissait pas —
          on tapait dessus sans effet (correction S67). */}
      <button
        type="button"
        onClick={onActivate}
        className="flex w-full items-center gap-3 px-4 text-left"
        style={{ paddingBlock: 16 }}
      >
        <CategoryMark initials={space.emblemInitials} hue={space.emblemHue} />

        <span className="min-w-0 grow">
          <span className="block truncate text-card-title font-semibold">{space.name}</span>
          <span className="mt-1 block text-meta text-ink-muted">
            {space.memberCount} {space.memberCount > 1 ? 'membres' : 'membre'} · {space.itemCount}{' '}
            {space.itemCount > 1 ? 'idées' : 'idée'}
          </span>
        </span>

        {space.isActive ? (
          <span className="shrink-0 text-meta font-semibold text-accent">Actif</span>
        ) : space.unseenCount > 0 ? (
          // Badge chiffré : « compte les nouveautés non vues, pas les éléments à classer ».
          <span
            className="flex shrink-0 items-center justify-center rounded-full bg-accent text-meta font-semibold text-card"
            style={{ minWidth: 24, height: 24 }}
          >
            {space.unseenCount}
          </span>
        ) : null}

        <span aria-hidden="true" className="shrink-0 text-ink-muted">
          ›
        </span>
      </button>

      {/* « Inviter » reste un lien à part : c'est une action rare, et la noyer dans la zone
          d'ouverture la rendrait déclenchable par erreur. */}
      <div className="px-4" style={{ paddingBottom: 16 }}>
        <button type="button" onClick={onInvite} className="text-meta font-semibold text-accent">
          Inviter quelqu'un
        </button>
      </div>
    </li>
  )
}

/** Parcours A, étape 3 : « Un lien à partager. Étape sautable. » */
function InvitationPanel({ invitation, onClose }: { invitation: Invitation; onClose: () => void }) {
  return (
    <section className="mt-6 rounded-card border border-border bg-accent-soft p-4">
      <p className="text-label font-semibold uppercase text-ink-muted">Code d'invitation</p>
      <p className="mt-2 font-serif text-record" style={{ letterSpacing: '0.12em' }}>
        {invitation.code}
      </p>
      <p className="mt-2 text-meta text-ink-muted">
        Valable jusqu'au {new Date(invitation.expiresAt).toLocaleDateString('fr-FR')}. Il ne sert
        qu'une fois.
      </p>
      <button type="button" onClick={onClose} className="mt-3 text-meta font-semibold text-accent">
        Fermer
      </button>
    </section>
  )
}

/** Feuille à un seul champ : création d'espace et adhésion partagent la même forme. */
function NameSheet({
  title,
  label,
  placeholder,
  action,
  mutate,
  onDone,
  onCancel,
}: {
  title: string
  label: string
  placeholder: string
  action: string
  mutate: (value: string) => Promise<SpaceSummary>
  onDone: () => void
  onCancel: () => void
}) {
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = useMutation({
    mutationFn: mutate,
    onSuccess: onDone,
    onError: (cause: unknown) =>
      setError(cause instanceof ApiError ? cause.message : "L'opération a échoué. Réessayez."),
  })

  return (
    <section className="mt-6 rounded-sheet border border-border bg-card p-4 shadow-sheet">
      <h2 className="font-serif text-record">{title}</h2>

      <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="valeur">
        {label}
      </label>
      <input
        id="valeur"
        value={value}
        autoFocus
        placeholder={placeholder}
        onChange={(event) => {
          setValue(event.target.value)
          setError(null)
        }}
        aria-invalid={error !== null}
        className="mt-2 w-full rounded-field border-[1.5px] bg-card px-4 outline-none"
        style={{
          height: 56,
          fontSize: 17,
          borderColor: error ? 'var(--color-danger)' : 'var(--color-accent)',
        }}
      />

      {error ? (
        <p className="mt-3 text-meta" style={{ color: 'var(--color-danger)' }}>
          {error}
        </p>
      ) : null}

      <button
        type="button"
        disabled={value.trim().length === 0 || submit.isPending}
        onClick={() => submit.mutate(value.trim())}
        className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
        style={{ height: 'var(--layout-primary-button)' }}
      >
        {submit.isPending ? 'Envoi…' : action}
      </button>

      <button
        type="button"
        onClick={onCancel}
        className="mt-3 w-full rounded-button border border-border bg-card text-card-title font-semibold"
        style={{ height: 'var(--layout-primary-button)' }}
      >
        Annuler
      </button>
    </section>
  )
}
